import React, { createContext, useContext } from "react";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import Button from "@mui/material/Button";
import LoadSpinner from "../../../../utilities/LoadSpinner";

// Shared layout for any pre-built report. The report component owns its own
// filter state (store / dates / job / etc.) and passes those inputs into
// `filters`. The shell renders the surrounding chrome: back button, title,
// filter row, print action, and a print-only stylesheet that hides the rest
// of the app when the user prints.

// Name of the store the active report runs against, provided by
// PrebuiltReportsDisplay so the printed header can say which store it covers
// without every report passing it through. Undefined for cross-store reports.
export const PrebuiltReportStoreNameContext = createContext(undefined);

export default function PrebuiltReportShell({
  report,
  onBack,
  filters,
  storePicker,
  actions,
  isLoading,
  children,
}) {
  const storeName = useContext(PrebuiltReportStoreNameContext);
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-white min-h-screen prebuilt-report-root">
      {/* Print-only styles: hide app chrome, expand content to full width */}
      <style>{`
        .prebuilt-report-print-only { display: none; }
        @media print {
          body * { visibility: hidden; }
          .prebuilt-report-root, .prebuilt-report-root * { visibility: visible; }
          .prebuilt-report-root { position: absolute; left: 0; top: 0; width: 100%; }
          .prebuilt-report-no-print { display: none !important; }
          .prebuilt-report-print-only { display: block !important; }
          .prebuilt-report-print-area { padding: 0 !important; }
        }
      `}</style>

      <div className="px-6 pt-4 pb-12 prebuilt-report-print-area">
        {/* Header: back button + title + actions */}
        <div className="flex items-start justify-between gap-6 mb-4 prebuilt-report-no-print sm:flex-col sm:gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <IconButton size="small" onClick={onBack} aria-label="Back">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                {report.name}
              </h3>
              {/* Capped so the description wraps before it crowds the
                  report's action buttons. */}
              <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">{report.description}</p>
            </div>
          </div>
          {/* Kept on one line on desktop; wraps on phones, where the three
              buttons are wider than the screen. */}
          <div className="flex shrink-0 items-center gap-2 sm:flex-wrap">
            {actions}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                borderColor: "#6d28d9",
                color: "#6d28d9",
                textTransform: "none",
                "&:hover": { borderColor: "#5b21b6", backgroundColor: "#f5f3ff" },
              }}
            >
              Print
            </Button>
          </div>
        </div>

        {/* Filter row (also no-print). The store picker comes from
            PrebuiltReportsDisplay so every store-scoped report shares one
            selection; it leads the row, separated from the report's own
            filters. */}
        {(filters || storePicker) && (
          <div className="mb-4 px-3 py-2 border border-slate-200 rounded-lg prebuilt-report-no-print">
            {storePicker ? (
              // items-end (not center) so the picker bottom-aligns with the
              // report's inputs whether its label is inline or stacked.
              <div className="flex flex-wrap items-end gap-3">
                <div className="pr-3 border-r border-slate-200 self-stretch flex items-end">
                  {storePicker}
                </div>
                {filters}
              </div>
            ) : (
              filters
            )}
          </div>
        )}

        {/* Print-only title (so the printed page has a header) */}
        <div className="prebuilt-report-print-only mb-4">
          <h2 className="text-lg font-semibold">{report.name}</h2>
          {storeName && <p className="text-sm text-gray-600">{storeName}</p>}
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="min-h-[calc(100vh-220px)] py-12 flex items-center justify-center">
            <LoadSpinner />
          </div>
        ) : (
          <div>{children}</div>
        )}
      </div>
    </div>
  );
}
